import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'

import { Button, Input } from 'antd'
import { IFsNodeSchema } from '@cloudike/web_files'
import classNames from 'classnames'
import { debounce } from 'lodash'
import { AssetsGrid } from 'features/assets/ui'
import { getFilteredBoardsSelector } from 'features/boards/model/selectors/boardsSelectors'
import { fetchAllBoardsThunk } from 'features/boards/model/slice/boardsSlice'
import { CaretDownFilled, CloseOutlined, PictureOutlined, SearchOutlined } from 'shared/ui'
import { BaseModal } from 'shared/ui/BaseModal'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { getTreeLikeDataFromNodes } from 'shared/utils/filesUtils'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { EmptyBoardPlaceholder } from 'features/common/ui/EmptyBoardPlaceholder'
import { getUploadingFilesSelector } from 'features/files-uploading/selectors/filesUploadingSelectors'
import { UPLOADER_LIST_TYPES } from 'features/files-uploading/model/slice/filesUploadingSlice'
import { useTranslation } from 'react-i18next'

import { 
  selectAttachmentsModalOpen, 
  selectAttachmentsModalSection,
  selectSelectedAttachments,
  selectAttachmentsLoading,
  selectAttachmentsSearchValue,
  selectAttachmentsSearching,
  selectAttachmentsSearchResults,
  selectAttachmentsAssets,
  selectAttachmentsAreAllAssetsFetched
} from '../model/selectors/aiAssistantSelectors'
import { aiAssistantSlice, fetchAiAssistantSearchResultsThunk, fetchAttachmentsBoardAllAssetsThunk, fetchFirstAttachmentsAssetsThunk, fetchNextAttachmentsAssetsThunk } from '../model/slice/aiAssistantSlice'

import { AssetsSkeleton } from './AssetsSkeleton'
interface MenuItemData {
  key?: React.Key
  label?: React.ReactNode
  icon?: React.ReactNode
  children?: MenuItemData[]
  type?: string
}

export const AddAttachmentsModal = () => {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectAttachmentsModalOpen)
  const selectedSection = useAppSelector(selectAttachmentsModalSection)
  const selectedAttachments = useAppSelector(selectSelectedAttachments)
  const attachmentsLoading = useAppSelector(selectAttachmentsLoading)
  const searchValue = useAppSelector(selectAttachmentsSearchValue)

  const isSearching = useAppSelector(selectAttachmentsSearching)
  
  const assets = useAppSelector(selectAttachmentsAssets)
  const searchAssets = useAppSelector(selectAttachmentsSearchResults)
  const boards = useAppSelector(getFilteredBoardsSelector)
  const areAllAssetsFetched = useAppSelector(selectAttachmentsAreAllAssetsFetched)
  const currentAttachments = useAppSelector(state => state.aiAssistant.attachments)
  const uploadingAiItems = getUploadingFilesSelector().filter((f: any) => f.uploadingSourceType === UPLOADER_LIST_TYPES.AI_ASSISTANT_ATTACHMENTS)
  const totalCount = (currentAttachments?.length || 0) + (uploadingAiItems?.length || 0) + (selectedAttachments?.length || 0)
  const isLimitReached = totalCount >= 6

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({})

  const { t } = useTranslation()

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  })

  const debouncedSearch = useMemo(
    () => debounce((value: string) => {
      if (value.trim().length > 0) {
        dispatch(aiAssistantSlice.actions.setAttachmentsSearching(true))

        dispatch(fetchAiAssistantSearchResultsThunk({ value })).finally(() => {
          dispatch(aiAssistantSlice.actions.setAttachmentsSearching(false))
        })
      } else {
        dispatch(aiAssistantSlice.actions.clearAttachmentsSearchResults())
      }
    }, 300),
    [dispatch]
  )

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchAllBoardsThunk())

      if (selectedSection === 'all-assets') {
        dispatch(fetchFirstAttachmentsAssetsThunk({ boardId: null }))
      }
    }
  }, [isOpen, dispatch])

  useEffect(() => {
    if (selectedSection === 'all-assets') {
      dispatch(fetchFirstAttachmentsAssetsThunk({ boardId: null }))
    } else if (selectedSection !== 'all-assets') {
      dispatch(fetchAttachmentsBoardAllAssetsThunk({ boardId: selectedSection }))
    }
  }, [selectedSection, dispatch])

  useEffect(() => {
    if (inView && selectedSection === 'all-assets' && !areAllAssetsFetched && !attachmentsLoading) {
      dispatch(fetchNextAttachmentsAssetsThunk())
    }
  }, [inView, selectedSection, areAllAssetsFetched, attachmentsLoading, dispatch])

  useEffect(() => {
    debouncedSearch(searchValue)

    return () => {
      debouncedSearch.cancel()
    }
  }, [searchValue, debouncedSearch])

  const handleClose = useCallback(() => {
    dispatch(aiAssistantSlice.actions.setAttachmentsModalOpen(false))
    dispatch(aiAssistantSlice.actions.setAttachmentsSearchValue(''))
    dispatch(aiAssistantSlice.actions.clearAttachmentsSearchResults())
    dispatch(aiAssistantSlice.actions.clearAttachmentSelection())
  }, [dispatch])

  const handleSectionSelect = useCallback((sectionId: string) => {
    dispatch(aiAssistantSlice.actions.setAttachmentsModalSection(sectionId))
  }, [dispatch])

  const handleAssetSelect = useCallback((asset: IFsNodeSchema) => {
    if (isLimitReached && !selectedAttachments.some(a => a.id === asset.id)) return

    dispatch(aiAssistantSlice.actions.toggleAttachmentSelection(asset))
  }, [dispatch, isLimitReached])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    dispatch(aiAssistantSlice.actions.setAttachmentsSearchValue(value))
  }, [dispatch])

  const toggleExpand = useCallback((e: React.MouseEvent, itemKey: string) => {
    e.stopPropagation()
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }))
  }, [])

  const renderMenuItem = useCallback((menuItem: MenuItemData, level = 1): React.ReactNode => {
    if (!menuItem || menuItem.type === 'divider' || !menuItem.key) {
      return null
    }

    const itemKey = String(menuItem.key)
    const hasChildren = menuItem.children && menuItem.children.length > 0
    const isExpanded = expandedItems[itemKey]
    const isSelected = selectedSection === itemKey

    return (
      <React.Fragment key={itemKey}>
        <SMenuItem
          className={classNames({ 'active-menu-item': isSelected })}
          level={level}
          onClick={() => handleSectionSelect(itemKey)}
        >
          <div className="menu-left-part">
            {hasChildren && (
              <div
                className="expand-icon"
                onClick={(e) => toggleExpand(e, itemKey)}
              >
                <CaretDownFilled className={isExpanded ? "icon-normal" : "icon-rotate"} />
              </div>
            )}

            <span className="menu-item-label">
              {menuItem.label}
            </span>
          </div>
        </SMenuItem>

        {hasChildren && (
          <SNestedMenu className={isExpanded ? "expanded" : ""}>
            {menuItem.children.map(childItem => 
              renderMenuItem(childItem, level + 1)
            )}
          </SNestedMenu>
        )}
      </React.Fragment>
    )
  }, [expandedItems, selectedSection, handleSectionSelect, toggleExpand])

  const getMenuData = useCallback((): MenuItemData[] => {
    const mapBoardFields = (board: any) => ({
      key: board.id,
      label: board.name,
      type: board.type
    })

    const boardsTree = getTreeLikeDataFromNodes(boards, mapBoardFields)

    return boardsTree
  }, [boards])

  const handleAdd = useCallback(() => {
    dispatch(aiAssistantSlice.actions.clearAttachmentSelection())
    dispatch(aiAssistantSlice.actions.addAttachments(selectedAttachments))

    handleClose()
  }, [selectedAttachments, handleClose])

  const assetsToShow = useMemo(() => {
    if (searchValue.trim().length > 0) {
      return searchAssets || []
    }

    return assets || []
  }, [searchValue, searchAssets, assets])

  return (
    <SModal
      isOpen={isOpen}
      onClose={handleClose}
      style={{ top: '64px' }}
      width={window.innerWidth}
      closeIcon={false}
      transitionName="ant-modal-slide-up"
    >
      <SModalContent>
        <SHeader>
          <SHeaderLeft>
            <SCloseButton
              type="text"
              icon={<SCloseIcon />}
              onClick={handleClose}
            />

            <SHeaderTitle>
            {selectedAttachments.length > 0 ? `Add assets (${selectedAttachments.length} selected)` : 'Add assets'}
            </SHeaderTitle>
          </SHeaderLeft>

          <SHeaderRight>
            <SSearchInput
              placeholder="Search"
              value={searchValue}
              onChange={handleSearchChange}
              prefix={<SearchOutlined />}
            />

            <Button
              type="primary" 
              onClick={handleAdd}
              disabled={selectedAttachments.length === 0}
            >
              Select
            </Button>
          </SHeaderRight>
        </SHeader>

        <SContent>
          <SSidebar>
            <SMenuContainer>
              <SMenuItem
                className={classNames({ 'active-menu-item': selectedSection === 'all-assets' })}
                level={1}
                onClick={() => handleSectionSelect('all-assets')}
              >
                <SAllAssetsIcon />

                <span className="menu-item-label">
                  {t("a_sidebar_allAssets")}
                </span>
              </SMenuItem>

              <SBoardsTitleContainer>
                Boards
              </SBoardsTitleContainer>

              {getMenuData().map(menuItem => renderMenuItem(menuItem))}
            </SMenuContainer>
          </SSidebar>

          <SMainContent>
            <SAssetsContainer ref={scrollContainerRef}>
              {isSearching && (
                <AssetsSkeleton />
              )}

              {!isSearching && (
                <>
                  {!attachmentsLoading && assetsToShow.length > 0 && (
                    <AssetsGrid
                      assets={assetsToShow}
                      selectedItemsIds={selectedAttachments.map(attachment => attachment.id)}
                      onSelect={handleAssetSelect}
                      withoutContextMenu
                      withoutOpenPreview
                      onContextStateChanged={() => false}
                      isOuterContextMenuOpened={false}
                      draggable={false}
                      scrollContainerNode={scrollContainerRef.current}
                    />
                  )}

                  {selectedSection === 'all-assets' && !areAllAssetsFetched && !attachmentsLoading && (
                    <div ref={loadMoreRef}
                      style={{ height: '1px' }}
                    />
                  )}

                  {attachmentsLoading && (
                    <AssetsSkeleton />
                  )}

                  {!attachmentsLoading && assetsToShow.length === 0 && (
                    <EmptyBoardPlaceholder texts={{
                      title: 'No assets found',
                      description: 'No assets found in this section',
                    }}
                    />
                  )}
                </>
              )}
            </SAssetsContainer>
          </SMainContent>
        </SContent>
      </SModalContent>
    </SModal>
  )
}

const SModal = styled(BaseModal)`
  max-width: 100vw !important;
  height: calc(100vh - 64px) !important;
  margin: 0 !important;
  padding: 0 !important;

  & > div {
    height: 100%;
  }

  .ant-modal-body {
    padding: 0;
    height: 100%;
  }

  .ant-modal-content {
    padding: 0;
    height: 100%;
  }
`

const SModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const SHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: white;
  z-index: 1;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
`

const SHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SCloseButton = styled(Button)`
  padding: 0;
  font-size: 20px;
`

const SHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const SCloseIcon = styled(CloseOutlined)`
  color: #00000073;
`

const SHeaderTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #262626;
`

const SContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`

const SSidebar = styled.div`
  width: 280px;
  border-right: 1px solid #f0f0f0;
  background: #fafafa;
  overflow-y: auto;
`

const SMenuContainer = styled.div`
  padding: 16px 0;
`

interface SCustomMenuItemProps {
  level: number;
}

const SMenuItem = styled.div<SCustomMenuItemProps>`
  min-height: 40px;
  height: 40px;
  display: flex;
  padding: 0px 15px 0px ${props => 23 * props.level}px;
  margin: 2px 4px 0;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.65);
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  border-radius: 8px;
  border: 2px solid transparent;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &.active-menu-item {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .menu-left-part {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
  }
  
  .menu-item-label {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  
  .menu-item-actions {
    display: none;
    gap: 6px;
    transition: display 0.4s;
    flex-shrink: 0;
    overflow: visible;
  }
  
  .action-icon {
    font-size: 18px;
    color: rgba(0, 0, 0, 0.45);
    
    &:hover {
      color: var(--color-primary);
    }
  }
  
  &:hover .menu-item-actions {
    display: flex;
  }
  
  &:hover .menu-left-part {
    max-width: calc(100% - 60px);
  }
  
  .expand-icon {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;
    
    .icon-rotate {
      transition: transform 0.2s;
      transform: rotate(-90deg);
    }
    
    .icon-normal {
      transition: transform 0.2s;
      transform: rotate(0);
    }
  }
`

const SNestedMenu = styled.div`
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;

  &.expanded {
    max-height: 1000px;
  }
`

const SMainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const SBoardsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  padding: 24px 15px 8px 24px;
  margin: 0 4px;
`

const SSearchInput = styled(Input)`
  .ant-input {
    border-radius: 8px;
  }
`

const SAssetsContainer = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  position: relative;
`

// removed unused SLoadingContainer

const SAllAssetsIcon = styled(PictureOutlined)`
  margin-right: 11px;
`